import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useAsync } from "@utils/hooks";
import { isEmail } from "@utils/regex";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { ErrorContainer } from "@ui/components/containers/errorContainer";
import { MailIcon } from "@ui/components/icons";
import { useForm } from "@ui/components/inputs/hooks";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
interface EmailChangeFormProps extends React.HTMLAttributes<HTMLFormElement> {
	oobCode: string;
}
export const EmailChangeForm: React.FC<EmailChangeFormProps> = ({ oobCode, ...htmlProps }) => {
	const { checkSubmit, fieldProps, formValue } = useForm({ email: "", confirm: "" }, ["email", "confirm"], {
		email: isEmail,
		confirm: (p, { email }) => p === email,
	});
	const { currentProfileService } = useServices();
	const history = useHistory();

	const { run, loading, error } = useAsync(async () => {
		try {
			await currentProfileService.updateEmail(formValue.email);
			createToast(I18n.t("success"), I18n.t("settings.change.email.success"), "success");
			history.push("/");
			currentProfileService.logout();
		} catch {
			createToast(I18n.t("error"), I18n.t("auth.error.default"), "error");
		}
	}, [formValue.email]);

	return (
		<Container
			{...htmlProps}
			onSubmit={async (e) => {
				e.preventDefault();
				if (!checkSubmit()) {
					return;
				}
				run();
				// createToast(I18n.t("success"), I18n.t("email.reset.success"), "success");
				// history.push("/");
			}}
		>
			<Title>{I18n.t("settings.change.email.label")}</Title>
			<Fields>
				<FieldWrapper>
					<label htmlFor="email-change-email">{I18n.t("settings.change.email.new.placeholder")}</label>
					<SimpleTextInput
						id="email-change-email"
						color="primary"
						icon={<MailIcon />}
						iconPosition="left"
						type="email"
						autoComplete="new-email"
						{...fieldProps("email")}
						placeholder={I18n.t("email.placeholder")}
					/>
				</FieldWrapper>
				<FieldWrapper>
					<label htmlFor="email-change-confirm">{I18n.t("settings.change.email.new.confirm.placeholder")}</label>
					<SimpleTextInput
						id="email-change-confirm"
						color="primary"
						icon={<MailIcon />}
						iconPosition="left"
						type="email"
						autoComplete="new-email"
						{...fieldProps("confirm")}
						placeholder={I18n.t("email.placeholder")}
					/>
				</FieldWrapper>
				{error && <ErrorContainer>{I18n.t("auth.error.default")}</ErrorContainer>}
			</Fields>
			<LoadingButton loading={loading} type="submit">
				{I18n.t("validate")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled.form`
	${gap(54)}
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
`;

const Fields = styled.div`
	${gap(30)}
`;

const FieldWrapper = styled.div`
	label {
		display: block;
		${({ theme }) => theme.textStyles.primaryMedium};
		margin-bottom: 16px;
	}
`;
